/**
 * シナリオ全体の境界丸め (BGM の小節頭に合わせてシーンの尺を丸める・計画 md §15.5) の表示用の純関数。
 * backend: services/scenario/generation/motion_boundaries.py
 */
import type { MotionBoundaryRoundingItem, MotionBoundaryRoundingResult } from '../../../features/scenarios/api';

/** 予定尺の整数保存についての注記 (プレビューに常に出す) */
export const MOTION_BOUNDARY_ROUNDING_NOTE =
  '予定尺は整数で保存されます。モーショングラフィックスは生成時に終端を小節頭に合わせ直します';

function formatSeconds(sec: number): string {
  return Number.isInteger(sec) ? String(sec) : sec.toFixed(1);
}

function formatShift(shift: number): string {
  const sign = shift > 0 ? '+' : shift < 0 ? '−' : '±';
  return `${sign}${Math.abs(shift).toFixed(2)} 秒`;
}

/** 「シーン 2: 8 秒 → 9 秒 (+0.81 秒・小節頭)」の 1 行。 */
export function formatBoundaryRoundingRow(
  item: MotionBoundaryRoundingItem,
  gridKind: MotionBoundaryRoundingResult['gridKind'],
): string {
  const label = `シーン ${item.order_number ?? '?'}`;
  const before = item.planned_sec ?? Math.round(item.before_sec);
  const after = item.after_sec ?? before;
  const gridLabel = gridKind === 'beats' ? '拍' : '小節頭';
  const detail = Math.abs(item.shift_sec) > 1e-6
    ? `${formatShift(item.shift_sec)}・${gridLabel}`
    : '総尺に合わせて調整';
  return `${label}: ${formatSeconds(before)} 秒 → ${formatSeconds(after)} 秒 (${detail})`;
}

/** 変更のあるシーンの行 (order 順)。 */
export function changedBoundaryRoundingRows(result: MotionBoundaryRoundingResult): Array<{ sceneId: string; text: string }> {
  return result.items
    .filter((it) => it.changed)
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map((it) => ({ sceneId: it.scene_id, text: formatBoundaryRoundingRow(it, result.gridKind) }));
}

/** 書き戻す予定尺 (scene_id → 整数秒)。変更のあるシーンだけ。 */
export function boundaryRoundingDurations(result: MotionBoundaryRoundingResult): Map<string, number> {
  const out = new Map<string, number>();
  for (const it of result.items) {
    if (it.changed && it.after_sec != null) out.set(it.scene_id, it.after_sec);
  }
  return out;
}

/** 丸めない / 丸められないときの理由 (トースト用)。 */
export function describeBoundaryRoundingSkip(result: MotionBoundaryRoundingResult): string {
  if (result.reason) return result.reason;
  if (result.changed === 0) return '変更が必要なシーンはありません (すでに小節頭に揃っています)';
  return 'シーンの尺を丸められませんでした';
}

/**
 * プレビューの前提になる入力の要約。これが変わったらプレビューを破棄して取り直す (レビュー反映 2026-09-28:
 * 適用 API は表示中の計画ではなく今の DB から再計算するため、BGM・尺・順序・動画が変わった後に古いプレビューを
 * 適用させない。backend も fingerprint で同じ突き合わせをする)。
 */
export function boundaryRoundingInputSignature(scenario: {
  bgm_material_id?: string | null;
  bgm_excluded?: boolean | null;
  scenes?: Array<{
    id?: string | null;
    order?: number | null;
    video_duration_seconds?: number | null;
    video_material_id?: string | null;
    video_excluded?: boolean | null;
    narration_duration_seconds?: number | null;
    brand_cut_position?: string | null;
    video_input_mode?: string | null;
  }> | null;
}): string {
  const sceneParts = [...(scenario.scenes || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => [
      s.id ?? '', s.order ?? '', s.video_duration_seconds ?? '', s.video_material_id ?? '', s.video_excluded ? 1 : 0,
      s.narration_duration_seconds ?? '', s.brand_cut_position ?? '', s.video_input_mode ?? '',
    ].join(':'));
  return [scenario.bgm_material_id ?? '', scenario.bgm_excluded ? 1 : 0, ...sceneParts].join('|');
}
